import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { prefetchRoute } from '@/lib/routePrefetch';
import { LayoutDashboard, Flag, Palette, Settings, CreditCard, X, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import api from '@/lib/api';
import { logger } from '@/lib/logger';

import { BILLING_HIDDEN_SOFT_LAUNCH } from '@/lib/softLaunchFlags';

const allNavItems = [
  { to: '/admin/dash', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/tournaments', icon: Flag, label: 'Tournaments' },
  { to: '/admin/season', icon: Trophy, label: 'Season', featureKey: 'season' },
];

const settingsMenuItems = [
  { to: '/admin/branding', icon: Palette, label: 'Branding' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
  // Billing nav link is hidden during soft-launch — flip the flag in
  // /app/frontend/src/lib/softLaunchFlags.js to re-enable.
  ...(BILLING_HIDDEN_SOFT_LAUNCH ? [] : [{ to: '/admin/billing', icon: CreditCard, label: 'Billing' }]),
];

function isPathActive(pathname, to) {
  return pathname === to || pathname.startsWith(to + '/');
}

const MobileNavItem = ({ to, icon: Icon, label, isActive, onClose }) => (
  <NavLink key={to} to={to} onClick={onClose}
    onMouseEnter={() => prefetchRoute(to)}
    onFocus={() => prefetchRoute(to)}
    data-testid={`sidebar-nav-${label.toLowerCase()}`}
    className={`group flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 px-3 py-2.5
      ${isActive
        ? 'bg-white/15 text-[hsl(var(--sidebar-foreground))] shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
        : 'text-[hsl(var(--sidebar-foreground))]/70 hover:bg-white/[0.08] hover:text-[hsl(var(--sidebar-foreground))]'
      }`}>
    <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
    <span>{label}</span>
  </NavLink>
);

const DesktopNavIcon = ({ to, icon: Icon, label, isActive }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <NavLink to={to}
        onMouseEnter={() => prefetchRoute(to)}
        onFocus={() => prefetchRoute(to)}
        data-testid={`sidebar-nav-${label.toLowerCase()}`}
        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200
          ${isActive
            ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      </NavLink>
    </TooltipTrigger>
    <TooltipContent side="right" sideOffset={8} className="font-medium text-xs">{label}</TooltipContent>
  </Tooltip>
);

const SettingsMenuItem = ({ to, icon: Icon, label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors
      ${isActive ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
    data-testid={`sidebar-settings-${label.toLowerCase()}`}
  >
    <Icon className="w-4 h-4" strokeWidth={1.8} />
    {label}
  </button>
);

export const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [logo, setLogo] = useState('');
  const [societyInitial, setSocietyInitial] = useState('F');
  const [menuOpen, setMenuOpen] = useState(false);
  // null = unknown (still loading). We start hidden and reveal only once the
  // API confirms the feature is enabled — avoids a flash for new signups
  // whose societies have `season_leaderboard_enabled: false` by default.
  const [seasonEnabled, setSeasonEnabled] = useState(null);

  useEffect(() => {
    // Fetch branding + season-settings in parallel. Season toggle drives
    // whether the Season nav item is rendered — disabled societies shouldn't
    // see a dead link in their sidebar.
    (async () => {
      const [brandRes, seasonRes] = await Promise.allSettled([
        api.get('/group/branding'),
        api.get('/group/season-settings'),
      ]);
      if (brandRes.status === 'fulfilled') {
        setLogo(brandRes.value.data?.logo || '');
        setSocietyInitial(brandRes.value.data?.name?.[0]?.toUpperCase() || 'F');
      } else {
        logger.warn('Sidebar logo fetch failed:', brandRes.reason?.message);
      }
      if (seasonRes.status === 'fulfilled') {
        setSeasonEnabled(seasonRes.value.data?.season_leaderboard_enabled === true);
      } else {
        // Fetch failed — keep the link hidden rather than risk showing a
        // dead nav item to a disabled society.
        setSeasonEnabled(false);
      }
    })();
  }, []);

  const navItems = allNavItems.filter(item => item.featureKey !== 'season' || seasonEnabled === true);

  if (onClose) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))]" data-testid="sidebar">
        <div className="flex items-center h-16 flex-shrink-0 px-5 gap-3">
          <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-9 h-9 rounded-xl object-contain flex-shrink-0" />
          <span className="font-heading text-lg font-semibold tracking-tight whitespace-nowrap">SportSweeps</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="ml-auto text-[hsl(var(--sidebar-foreground))] hover:bg-white/10 h-8 w-8" data-testid="sidebar-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <nav className="flex-1 py-1 space-y-0.5 px-3" data-testid="sidebar-nav">
          {[...navItems, ...settingsMenuItems].map(({ to, icon, label }) => (
            <MobileNavItem key={to} to={to} icon={icon} label={label} isActive={isPathActive(location.pathname, to)} onClose={onClose} />
          ))}
        </nav>
      </div>
    );
  }

  const isSettingsActive = settingsMenuItems.some(item => isPathActive(location.pathname, item.to));

  return (
    <div className="flex flex-col h-full items-center pl-5 pt-4" data-testid="sidebar">
      {/* SportSweeps Logo */}
      <div className="mb-4" data-testid="sidebar-brand-logo">
        <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-10 h-10 rounded-xl object-contain" />
      </div>

      <nav className="flex flex-col items-center gap-1.5" data-testid="sidebar-nav">
        <TooltipProvider delayDuration={0}>
          {navItems.map(({ to, icon, label }) => (
            <DesktopNavIcon key={to} to={to} icon={icon} label={label} isActive={isPathActive(location.pathname, to)} />
          ))}
        </TooltipProvider>
      </nav>

      <div className="flex-1" />
    </div>
  );
};
